/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { handleCallApiError, handleCallApiSuccess, hideLoading, showLoading } from 'redux/slices/commonSlice';
import store, { AppDispatch } from 'redux/store';
import COMMON from 'constants/common';
import MESSAGES from 'constants/messages';

type SuccessResponse<T> = {
  success: boolean;
  message: string;
  data: T;
};

type ErrorResponse = {
  success: boolean;
  logId: string;
  errorCode: string;
  message: string;
  errors: { [key: string]: string };
};

const addCheckAuth = (params = {}) => {
  const { userInfo } = store.getState().common;
  const newParams = {
    ...params,
    chkAuthId: userInfo?.userId,
    chkAuthHeadQId: userInfo?.currentHeadQId,
    chkAuthStoreId: userInfo?.currentStoreId,
  };
  return newParams;
};

function encode(val: string | number | boolean) {
  return encodeURIComponent(val)
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+')
    .replace(/%5B/gi, '[')
    .replace(/%5D/gi, ']');
}

/**
 * Encodes a single query param
 */
function encodeAxiosParam(value: any) {
  let v = value;
  if (toString.call(value) === '[object Date]') {
    v = value.toISOString();
  } else if (typeof value === 'object') {
    v = JSON.stringify(value);
  }
  return encode(v);
}

/**
 * EOL対応: serializes params to a query string like axios pre-v1
 * https://github.com/axios/axios/issues/5630#issuecomment-1859776798
 *
 * {
 *   object: { a: 1 },                        // object=%7B%22a%22:1%7D
 *   arrayOfArray: [[1, 2, ","], [2]],        // arrayOfArray[]=[1,2,%22,%22]&arrayOfArray[]=[2]
 *   arrayOfNullUndefined: [null, undefined], // arrayOfNullUndefined[]=null&arrayOfNullUndefined[]=undefined
 *   arrayOfObject: [{ id: 1 }, { id: 12 }],  // arrayOfObject[]=%7B%22id%22:1%7D&arrayOfObject[]=%7B%22id%22:12%7D
 *   arrayOfString: ["a", "b", "c"],          // arrayOfString[]=a&arrayOfString[]=b&arrayOfString[]=c
 *   undefinable: undefined,                  // X
 *   nullable: null,                          // X
 *   emptyArray: [],                          // X
 *   emptyString: "",                         // emptyString=
 *   emptyObject: {},                         // emptyObject=%7B%7D
 *   date: new Date("1970-01-01"),            // date=1970-01-01T00:00:00.000Z
 * }
 */
function paramsSerializer(params: Record<string, any>) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : true))
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${key}[]=${encodeAxiosParam(v)}`).join('&');
      }
      return `${key}=${encodeAxiosParam(value)}`;
    })
    .join('&');
}

export default class HttpConnection {
  dispatch: AppDispatch;

  client: AxiosInstance;

  constructor(thunkAPI: { dispatch: AppDispatch }, isShowLoadingScreen = true) {
    this.dispatch = thunkAPI.dispatch;
    this.client = applyCaseMiddleware(
      axios.create({
        baseURL: COMMON.BASE_URL,
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
        withXSRFToken: true,
        paramsSerializer: paramsSerializer,
      }),
    );

    if (isShowLoadingScreen) {
      this.dispatch(showLoading());
    }
  }

  async get<T>(url: string, params = {}): Promise<T> {
    try {
      const newParams = addCheckAuth(params);
      const response = await this.client.get<SuccessResponse<T>>(url, { params: newParams });

      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleErrorResponse(error);
    }
  }

  async getFile(url: string, params = {}): Promise<any> {
    try {
      const newParams = addCheckAuth(params);
      const config: AxiosRequestConfig = {
        params: newParams,
        responseType: 'blob',
      };
      const response = await this.client.get<SuccessResponse<any>>(url, config);
      return this.handleDownloadFileResponse(response);
    } catch (error: any) {
      return this.handleDownloadFileErrorResponse(error);
    }
  }

  async getFileWithHeader(url: string, params = {}): Promise<any> {
    try {
      const newParams = addCheckAuth(params);
      // Step1: Call api to validate file can download or not
      await this.client.get<SuccessResponse<any>>(url, { params: newParams });
      const config: AxiosRequestConfig = {
        params: newParams,
        responseType: 'blob',
      };
      // If no error at step 1 => call api to download file with type is blob
      const response = await this.client.get<SuccessResponse<any>>(url, config);
      return { data: this.handleDownloadFileResponse(response), headers: response.headers };
    } catch (error: any) {
      return this.handleDownloadFileWithHeaderErrorResponse(error);
    }
  }

  async postDownloadFileWithHeader(url: string, data = {}): Promise<any> {
    try {
      const newData = addCheckAuth(data);
      // Step1: Call api to validate file can download or not
      await this.client.post<SuccessResponse<any>>(url, newData);
      const config: AxiosRequestConfig = {
        responseType: 'blob',
      };
      // If no error at step 1 => call api to download file with type is blob
      const response = await this.client.post<SuccessResponse<any>>(url, newData, config);
      return { data: this.handleDownloadFileResponse(response), headers: response.headers };
    } catch (error: any) {
      return this.handleDownloadFileWithHeaderErrorResponse(error);
    }
  }

  /**
   * Call api as POST method to download file with type is blob
   *
   * @param url The API URL
   * @param data The request body
   * @returns the object containing the file data and headers
   */
  async postFileWithHeader(url: string, data: any = {}): Promise<any> {
    // We don't need to call API 2 times to check whether the file can be downloaded or not
    // Since we already have the error response as Blob and content type is json
    // We can parse the response text to json and get the error message instead to reduce the number of API calls
    try {
      const newData = addCheckAuth(data);
      const config: AxiosRequestConfig = {
        responseType: 'blob',
      };
      // Call api to download file with type is blob
      const response = await this.client.post<SuccessResponse<any>>(url, newData, config);

      return { data: this.handleDownloadFileResponse(response), headers: response.headers };
    } catch (error: any) {
      // If error response is blob and content type is json
      // => read response as text
      // => parse response text to json
      if (
        error.response?.config?.responseType === 'blob' &&
        error.response?.data instanceof Blob &&
        error.response?.headers?.contentType === 'application/json'
      ) {
        const responseText = await error.response.data.text();
        try {
          const responseJson = JSON.parse(responseText);
          error.response.data = responseJson;
        } catch (_) {
          // NOP
        }
      }
      return this.handleDownloadFileWithHeaderErrorResponse(error);
    }
  }

  async getWithoutHandle<T>(url: string, params = {}): Promise<T> {
    try {
      const response = await this.client.get<SuccessResponse<T>>(url, { params });
      return response.data.data;
    } finally {
      this.dispatch(hideLoading());
    }
  }

  async post<T>(url: string, data: any = {}): Promise<T> {
    try {
      const newData = addCheckAuth(data);
      const response = await this.client.post<SuccessResponse<T>>(url, newData);

      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleErrorResponse(error);
    }
  }

  // only apply for api have status code 202
  async postSecond(url: string, data: any = {}): Promise<any> {
    try {
      const newData = addCheckAuth(data);
      const response = await this.client.post(url, newData);

      if (response.status === 202) {
        return this.handleMesagesErrorResponse(response);
      }

      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleErrorResponse(error);
    }
  }

  async put<T>(url: string, params = {}, data: any = {}): Promise<T> {
    try {
      const newData = addCheckAuth(data);
      const response = await this.client.put<SuccessResponse<T>>(url, newData, { params });

      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleErrorResponse(error);
    }
  }

  async delete<T>(url: string, params = {}): Promise<T> {
    try {
      const newParams = addCheckAuth(params);
      const response = await this.client.delete<SuccessResponse<T>>(url, { params: newParams });

      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleErrorResponse(error);
    }
  }

  handleResponse<T>(response: AxiosResponse<SuccessResponse<T>>): T {
    this.dispatch(hideLoading());
    this.dispatch(handleCallApiSuccess());

    return response.data.data;
  }

  handleDownloadFileResponse<T>(response: AxiosResponse<SuccessResponse<T>>): any {
    this.dispatch(hideLoading());
    this.dispatch(handleCallApiSuccess());

    return response.data;
  }

  handleErrorResponse(error: AxiosError<ErrorResponse>): never {
    this.dispatch(hideLoading());
    this.dispatch(
      handleCallApiError({ message: error.message, status: error.response?.status, data: error.response?.data }),
    );
    throw error;
  }

  handleMesagesErrorResponse(response: AxiosResponse<ErrorResponse>): any {
    this.dispatch(hideLoading());
    return response.data;
  }

  handleDownloadFileErrorResponse(error: AxiosError<ErrorResponse>): never {
    this.dispatch(hideLoading());
    this.dispatch(
      handleCallApiError({
        message: error.message,
        status: error.response?.status,
        data: { errors: { 0: [MESSAGES.ERR602] } },
      }),
    );
    throw error;
  }

  handleDownloadFileWithHeaderErrorResponse(error: AxiosError<ErrorResponse>): never {
    this.dispatch(hideLoading());
    this.dispatch(
      handleCallApiError({
        message: error.message,
        status: error.response?.status,
        data: { errors: { ...error.response?.data.errors } },
      }),
    );
    throw error;
  }

  async postFormData<T>(url: string, data: any = {}): Promise<T> {
    try {
      const newData = addCheckAuth(data);
      this.client = applyCaseMiddleware(
        axios.create({
          baseURL: COMMON.BASE_URL,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
          withXSRFToken: true,
          paramsSerializer: paramsSerializer,
        }),
      );
      const formData = new FormData();
      const objectEntries = Object.entries(newData);
      objectEntries.forEach(([key, value]) => {
        const newValue: any = value;
        formData.append(key, newValue);
      });
      const response = await this.client.post<SuccessResponse<T>>(url, formData);
      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleErrorResponse(error);
    }
  }
}
